import dayjs from 'dayjs';
import Lang from '/@/modules/core/app/services/Lang';
import { BaseBeneficiaryMeta } from '/@/modules/estateaccount/models/meta/BaseBeneficiaryMeta';
import { BeneficiaryGuardianCategory } from '/@/modules/beneficiary/enums/BeneficiaryGuardianCategory';
import { PartnerMetaBooleanProp } from '/@/modules/guidancestep/enums/PartnerMetaBooleanProp';
import { DateFormat } from '@/modules/core/app/constants/date.constants';
import { PlanningBeneficiaryCaredBy } from '@/modules/beneficiary/enums/PlanningBeneficiaryCaredBy';

export class BeneficiaryAdultMeta extends BaseBeneficiaryMeta {
  /**
   * @var {string}
   */
  relationshipType;

  /**
   * @var {string}
   */
  otherRelationshipType;

  /**
   * @var {boolean}
   */
  hasMarriageCertificate;

  /**
   * @var {string}
   */
  missingMarriageCertificateNote;

  /**
   * @var {boolean}
   */
  hasMarriageContract;

  /**
   * @var {string}
   */
  missingMarriageContractNote;

  /**
   * @var {boolean}
   */
  hasDomesticContract;

  /**
   * @var {boolean}
   */
  marriageContractHasTestamentaryClause;

  /**
   * @var {boolean}
   */
  hasCommonLawChildren;

  /**
   * @var {boolean}
   */
  hasJointResidentialProperty;

  /**
   * @var {boolean}
   */
  hasJointInvestmentProperty;

  /**
   * @var {boolean}
   */
  hasJointFinancialInstitutionAccount;

  /**
   * @var {boolean}
   */
  hasSameAddressOfResidence;

  /**
   * @var {boolean}
   */
  deceasedIsBeneficiaryOfLifeInsurance;

  /**
   * @var {boolean}
   */
  isBeneficiaryOfDeceasedLifeInsurance;

  /**
   * @var {boolean}
   */
  otherDocumentaryEvidence;

  /**
   * @var {boolean}
   */
  isFullTimeStudent;

  /**
   * @var {boolean}
   */
  isReceivingCpp;

  /**
   * @var {boolean}
   */
  isReceivingQpp;

  /**
   * @var {boolean}
   */
  isSupportingAllChildren;

  /**
   * @var {string}
   */
  isSupportingAllChildrenExplanation;

  /**
   * @var {string}
   */
  evidenceOfCommonLawRelationship;

  /**
   * @var {Date}
   */
  cohabitationStartDate;

  /**
   * @var {Date}
   */
  cohabitationEndDate;

  /**
   * @var {boolean}
   */
  wasUnbornAtDeath;

  /**
   * @var {boolean}
   */
  hasMentalDisability;

  /**
   * @var {boolean}
   */
  isNotServed;

  /**
   * @var {string}
   */
  unservedReason;

  /**
   * @var {string}
   */
  otherUnservedReason;

  /**
   * @var {boolean}
   */
  hasLegalGuardian;

  /**
   * @var {boolean}
   */
  hasCareGiver;

  /**
   * @var {boolean}
   */
  hasBiologicalCareGiver;

  /**
   * @var {?string}
   */
  guardianCategory;

  /**
   * @var {?string}
   */
  caredByGovernmentEntity;

  /**
   * @var {Date}
   */
  marriageTerminationDate;

  /**
   * @var {string}
   */
  marriageTerminationReason;

  /**
   * @var {boolean}
   */
  hasCohabitationAgreement;

  /**
   * @var {boolean}
   */
  isNextOfKin;

  /**
   * @var {boolean}
   */
  willActAsPersonalRepresentative;

  /**
   * @var {string}
   */
  reasonForExecutorship;

  /**
   * @var {string}
   */
  reasonForExecutorshipOtherExplanation;

  constructor(model = {}) {
    super(model);

    this.relationshipType = model.relationshipType;
    this.otherRelationshipType = model.otherRelationshipType;
    this.hasMarriageCertificate = model.hasMarriageCertificate;
    this.missingMarriageCertificateNote = model.missingMarriageCertificateNote;
    this.hasMarriageContract = model.hasMarriageContract;
    this.missingMarriageContractNote = model.missingMarriageContractNote;
    this.hasDomesticContract = model.hasDomesticContract;
    this.marriageContractHasTestamentaryClause = model.marriageContractHasTestamentaryClause;
    this.hasCommonLawChildren = model.hasCommonLawChildren;
    this.hasJointResidentialProperty = model.hasJointResidentialProperty;
    this.hasJointInvestmentProperty = model.hasJointInvestmentProperty;
    this.hasJointFinancialInstitutionAccount = model.hasJointFinancialInstitutionAccount;
    this.hasSameAddressOfResidence = model.hasSameAddressOfResidence;
    this.deceasedIsBeneficiaryOfLifeInsurance = model.deceasedIsBeneficiaryOfLifeInsurance;
    this.isBeneficiaryOfDeceasedLifeInsurance = model.isBeneficiaryOfDeceasedLifeInsurance;
    this.otherDocumentaryEvidence = model.otherDocumentaryEvidence;
    this.isFullTimeStudent = model.isFullTimeStudent;
    this.isReceivingCpp = model.isReceivingCpp;
    this.isReceivingQpp = model.isReceivingQpp;
    this.isSupportingAllChildren = model.isSupportingAllChildren;
    this.isSupportingAllChildrenExplanation = model.isSupportingAllChildrenExplanation;
    this.evidenceOfCommonLawRelationship = model.evidenceOfCommonLawRelationship;
    this.cohabitationStartDate = model.cohabitationStartDate;
    this.cohabitationEndDate = model.cohabitationEndDate;
    this.wasUnbornAtDeath = model.wasUnbornAtDeath;
    this.hasMentalDisability = model.hasMentalDisability;
    this.isNotServed = model.isNotServed;
    this.unservedReason = model.unservedReason;
    this.otherUnservedReason = model.otherUnservedReason;
    this.hasLegalGuardian = model.hasLegalGuardian;
    this.hasCareGiver = model.hasCareGiver;
    this.hasBiologicalCareGiver = model.hasBiologicalCareGiver;
    this.guardianCategory = this.hasLegalGuardian ? model.guardianCategory : null;
    this.marriageTerminationDate = model.marriageTerminationDate;
    this.marriageTerminationReason = model.marriageTerminationReason;
    this.hasCohabitationAgreement = model.hasCohabitationAgreement;
    this.isNextOfKin = model.isNextOfKin;
    this.willActAsPersonalRepresentative = model.willActAsPersonalRepresentative;
    this.reasonForExecutorship = model.reasonForExecutorship;
    this.reasonForExecutorshipOtherExplanation = model.reasonForExecutorshipOtherExplanation;

    this.setCaredByGovernmentEntity(model.caredByGovernmentEntity);
  }

  get displayCohabitationDuration() {
    if (!this.cohabitationStartDate || !this.cohabitationEndDate) {
      return '';
    }

    return `${dayjs(this.cohabitationStartDate).format(DateFormat.ISO)} ${Lang.t(
      'core.to'
    )} ${dayjs(this.cohabitationEndDate).format(DateFormat.ISO)}`;
  }

  get availablePartnerMetaPropKeys() {
    const checkedKeys = [];
    PartnerMetaBooleanProp.getValuesByProp('key').forEach((key) => {
      if (this[key] === true) {
        checkedKeys.push(key);
      }
    });

    return checkedKeys;
  }

  setCaredByGovernmentEntity(value) {
    this.caredByGovernmentEntity =
      BeneficiaryGuardianCategory.isUnderPublicGuardianship(this.guardianCategory) ||
      PlanningBeneficiaryCaredBy.isCaredByGovernmentAgency(this.guardianCategory)
        ? value
        : '';
  }
}
